const normalizeText = (value) => String(value || '').trim();

const escapeHtml = (value) =>
  normalizeText(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toMoney = (value, currencySymbol = '€') => {
  const amount = toNumber(value);
  return `${currencySymbol}${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const formatInvoiceDate = (value, locale = 'en-GB') => {
  if (!value) return '';
  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) return normalizeText(value);
  return dateValue.toLocaleDateString(locale);
};

export const getInvoiceNumber = (transaction = {}) => {
  if (normalizeText(transaction.invoiceNumber)) return normalizeText(transaction.invoiceNumber);
  if (normalizeText(transaction.receiptNumber)) return normalizeText(transaction.receiptNumber);
  const idPart = normalizeText(transaction.id).slice(-6).toUpperCase();
  if (idPart) return `INV-${idPart}`;
  return `INV-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`;
};

export const getInvoiceFileName = (transaction = {}) => {
  const invoiceNumber = getInvoiceNumber(transaction)
    .replace(/[^a-z0-9-]+/gi, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return `${invoiceNumber || 'invoice'}.html`;
};

const buildInvoiceLineItems = (transaction = {}) => {
  if (Array.isArray(transaction.splitItems) && transaction.splitItems.length > 0) {
    return transaction.splitItems
      .map((item) => ({
        description: normalizeText(item.description || item.name || transaction.description || 'Service'),
        amount: toNumber(item.amount || item.grossAmount || item.total)
      }))
      .filter((item) => item.description || item.amount > 0);
  }

  if (Array.isArray(transaction.categoryBreakdown) && transaction.categoryBreakdown.length > 0) {
    return transaction.categoryBreakdown
      .map((item) => ({
        description: normalizeText(item.label || item.description || item.category || transaction.description || 'Service'),
        amount: toNumber(item.amount || item.grossAmount || item.total)
      }))
      .filter((item) => item.description || item.amount > 0);
  }

  return [{
    description: normalizeText(transaction.service || transaction.description || 'Service'),
    amount: toNumber(transaction.saleTotalAmount ?? transaction.invoiceTotalAmount ?? transaction.grossAmount ?? transaction.amount)
  }];
};

const getInvoiceAmounts = (transaction = {}) => {
  const gross = toNumber(transaction.saleTotalAmount ?? transaction.invoiceTotalAmount ?? transaction.grossAmount ?? transaction.amount);
  const discount = Math.max(toNumber(transaction.discountAmount), 0);
  const vat = Math.max(toNumber(transaction.invoiceVatAmount ?? transaction.vatAmount), 0);
  const subtotal = Math.max(gross - vat, 0);
  const paid = Math.max(toNumber(transaction.amountPaid ?? transaction.amount), 0);
  const outstanding = Math.max(toNumber(transaction.outstandingAmount ?? (gross - paid)), 0);

  return {
    gross,
    discount,
    vat,
    subtotal,
    paid: Math.min(paid, gross),
    outstanding
  };
};

export const buildInvoiceHtml = ({
  transaction = {},
  client = {},
  businessProfile = {},
  currencySymbol = '€',
  taxName = 'VAT',
  dateLocale = 'en-GB'
} = {}) => {
  const invoiceNumber = getInvoiceNumber(transaction);
  const invoiceDate = transaction.invoiceDate || transaction.date;
  const dueDate = transaction.dueDate || '';
  const sellerName = normalizeText(businessProfile.tradingName || businessProfile.businessName) || 'Business';
  const sellerAddress = normalizeText(businessProfile.businessAddress);
  const taxReference = normalizeText(businessProfile.taxReference);
  const companyNumber = normalizeText(businessProfile.companyRegistrationNumber);
  const clientName = normalizeText(client.name || transaction.clientName || transaction.customerName) || 'Client';
  const clientEmail = normalizeText(client.email);
  const clientPhone = normalizeText(client.phone);
  const lineItems = buildInvoiceLineItems(transaction);
  const amounts = getInvoiceAmounts(transaction);
  const vatRate = toNumber(transaction.invoiceVatRate ?? transaction.vatRate);
  const vatRateLabel = vatRate > 0 ? `${(vatRate > 1 ? vatRate : vatRate * 100).toFixed(1)}%` : '';
  const notes = normalizeText(transaction.notes);
  const jobReference = normalizeText(transaction.jobReference);

  const lineRows = lineItems.map((item) => `
        <tr>
          <td>${escapeHtml(item.description)}</td>
          <td class="amount">${escapeHtml(toMoney(item.amount, currencySymbol))}</td>
        </tr>
      `).join('');

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Invoice ${escapeHtml(invoiceNumber)}</title>
  <style>
    :root { color: #111827; font-family: Arial, Helvetica, sans-serif; }
    body { margin: 0; background: #f3f4f6; }
    .page { max-width: 840px; margin: 24px auto; background: #fff; padding: 44px; box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12); }
    .top { display: flex; justify-content: space-between; gap: 32px; border-bottom: 2px solid #111827; padding-bottom: 24px; }
    h1 { margin: 0; font-size: 34px; letter-spacing: 0; }
    h2 { margin: 0 0 8px; font-size: 14px; text-transform: uppercase; color: #4b5563; letter-spacing: 0; }
    p { margin: 4px 0; line-height: 1.45; }
    .muted { color: #6b7280; }
    .meta { text-align: right; min-width: 220px; }
    .section { margin-top: 28px; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; }
    th { text-align: left; background: #f9fafb; color: #4b5563; font-size: 12px; text-transform: uppercase; }
    th, td { padding: 12px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
    .amount { text-align: right; white-space: nowrap; }
    .totals { width: 320px; margin-left: auto; margin-top: 18px; }
    .totals div { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
    .totals .grand { font-weight: 700; font-size: 18px; border-bottom: 2px solid #111827; }
    .actions { max-width: 840px; margin: 24px auto 0; text-align: right; }
    button { border: 0; border-radius: 6px; background: #111827; color: #fff; padding: 10px 14px; font-weight: 700; cursor: pointer; }
    @media print {
      body { background: #fff; }
      .page { margin: 0; max-width: none; box-shadow: none; padding: 24px; }
      .actions { display: none; }
    }
    @media (max-width: 720px) {
      .page { margin: 0; padding: 24px; }
      .top, .grid { display: block; }
      .meta { text-align: left; margin-top: 20px; }
      .totals { width: 100%; }
    }
  </style>
</head>
<body>
  <div class="actions"><button type="button" onclick="window.print()">Print or Save as PDF</button></div>
  <main class="page">
    <section class="top">
      <div>
        <h1>Invoice</h1>
        <p class="muted">${escapeHtml(sellerName)}</p>
      </div>
      <div class="meta">
        <p><strong>${escapeHtml(invoiceNumber)}</strong></p>
        <p>Invoice date: ${escapeHtml(formatInvoiceDate(invoiceDate, dateLocale) || 'Not set')}</p>
        ${dueDate ? `<p>Due date: ${escapeHtml(formatInvoiceDate(dueDate, dateLocale))}</p>` : ''}
        ${jobReference ? `<p>Job: ${escapeHtml(jobReference)}</p>` : ''}
      </div>
    </section>

    <section class="section grid">
      <div>
        <h2>From</h2>
        <p><strong>${escapeHtml(sellerName)}</strong></p>
        ${sellerAddress ? `<p>${escapeHtml(sellerAddress).replace(/\n/g, '<br />')}</p>` : ''}
        ${taxReference ? `<p>${escapeHtml(taxName)} no: ${escapeHtml(taxReference)}</p>` : ''}
        ${companyNumber ? `<p>Company no: ${escapeHtml(companyNumber)}</p>` : ''}
      </div>
      <div>
        <h2>Bill To</h2>
        <p><strong>${escapeHtml(clientName)}</strong></p>
        ${clientEmail ? `<p>${escapeHtml(clientEmail)}</p>` : ''}
        ${clientPhone ? `<p>${escapeHtml(clientPhone)}</p>` : ''}
      </div>
    </section>

    <section class="section">
      <h2>Details</h2>
      <table>
        <thead>
          <tr>
            <th>Description</th>
            <th class="amount">Amount</th>
          </tr>
        </thead>
        <tbody>${lineRows}</tbody>
      </table>

      <div class="totals">
        ${amounts.discount > 0 ? `<div><span>Discount</span><span>-${escapeHtml(toMoney(amounts.discount, currencySymbol))}</span></div>` : ''}
        <div><span>Subtotal</span><span>${escapeHtml(toMoney(amounts.subtotal, currencySymbol))}</span></div>
        <div><span>${escapeHtml(taxName)}${vatRateLabel ? ` (${escapeHtml(vatRateLabel)})` : ''}</span><span>${escapeHtml(toMoney(amounts.vat, currencySymbol))}</span></div>
        <div class="grand"><span>Total</span><span>${escapeHtml(toMoney(amounts.gross, currencySymbol))}</span></div>
        <div><span>Paid</span><span>${escapeHtml(toMoney(amounts.paid, currencySymbol))}</span></div>
        <div><span>Outstanding</span><span>${escapeHtml(toMoney(amounts.outstanding, currencySymbol))}</span></div>
      </div>
    </section>

    ${notes ? `<section class="section"><h2>Notes</h2><p>${escapeHtml(notes).replace(/\n/g, '<br />')}</p></section>` : ''}
  </main>
</body>
</html>`;
};

export const downloadInvoiceHtml = (invoiceOptions = {}) => {
  if (typeof document === 'undefined' || typeof URL === 'undefined') return false;

  const html = buildInvoiceHtml(invoiceOptions);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = getInvoiceFileName(invoiceOptions.transaction);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
};
